/**
 * ICR (Intelligence Collection Request) CRUD.
 * Delegates to /api/intelligence-requests?type=ICR on dash-api.
 * Function names keep createIcr / getIcrByNumber for minimal churn; type is ICR.
 */

import dayjs from 'dayjs';

const BASE = process.env.REACT_APP_AWP_API_URL || 'https://awp.minsky.ai';

function authHeaders() {
    const token = localStorage.getItem('access_token');
    return token
        ? {'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json'}
        : {'Content-Type': 'application/json'};
}

async function apiFetch(path, opts = {}) {
    const resp = await fetch(`${BASE}${path}`, {headers: authHeaders(), ...opts});
    if (resp.status === 404) return null;
    if (!resp.ok) {
        let detail = `${opts.method || 'GET'} ${path} → ${resp.status}`;
        try {
            const body = await resp.json();
            if (body?.error) detail = body.error;
        } catch {
            /* ignore */
        }
        throw new Error(detail);
    }
    if (resp.status === 204) return null;
    return resp.json();
}

/** ICR document number from payload or icrs.icr_number. */
export function resolveIcrNumber(icr) {
    if (!icr) return '';
    return icr.icrNumber || '';
}

/** Linked ICR on a FINTEL/report. meta.icrNumber only. */
export function resolveReportLinkedIcrNumber(report) {
    if (!report) return null;
    return report.meta?.icrNumber || report.icrNumber || null;
}

/** Route param for ICR detail: number when present, otherwise document id (pending proposals). */
export function resolveIcrRouteKey(icr) {
    if (!icr) return '';
    return resolveIcrNumber(icr) || icr.id || '';
}

export function generateIcrNumber({date = new Date()} = {}) {
    const yyyy = dayjs(date).format('YYYY');
    const seq = Math.floor(1000 + Math.random() * 9000);
    return `ICR-${yyyy}-${seq}`;
}

export async function ensureUniqueIcrNumber(base) {
    let candidate = base;
    let counter = 1;
    // eslint-disable-next-line no-constant-condition
    while (true) {
        const existing = await apiFetch(
            `/api/intelligence-requests/by-icr-number/${encodeURIComponent(candidate)}`
        );
        if (!existing) return candidate;
        counter += 1;
        candidate = `${base}-${counter}`;
    }
}

export async function createIcr(data = {}) {
    const now = new Date();
    const title = data.title || data.subject || 'Untitled ICR';
    const question = data.question || data.informationRequirement || '';
    const timeframe = data.timeframe && typeof data.timeframe === 'object'
        ? data.timeframe
        : {start: null, due: data.expirationDate || null};
    const due = timeframe.due || data.expirationDate || null;

    const payload = {
        title,
        subject: title,
        question,
        informationRequirement: question,
        eeis: Array.isArray(data.eeis) ? data.eeis : [],
        supportedDecision: data.supportedDecision || '',
        priority: data.priority || 'Routine',
        owner: data.owner || '',
        timeframe: {
            start: timeframe.start || null,
            due: due ? dayjs(due).toISOString() : null,
        },
        expirationDate: due ? dayjs(due).toISOString() : null,
        areaOfInterest: data.areaOfInterest || '',
        classification: data.classification || 'UNCLASSIFIED',
        tags: Array.isArray(data.tags) ? data.tags : [],
        targets: Array.isArray(data.targets)
            ? data.targets.map((t) => (typeof t === 'string' ? t : t?.entityRef)).filter(Boolean)
            : [],
        currentAssessment: data.currentAssessment || '',
        createdAt: dayjs(now).toISOString(),
        createdBy: data.createdBy || null,
        sourceReportId: data.sourceReportId || null,
        sourceProductType: data.sourceProductType || null,
        workflowStatus: data.workflowStatus || 'Active',
        dtg: data.dtg || (() => {
            const d = new Date(now);
            const dd = String(d.getUTCDate()).padStart(2, '0');
            const hh = String(d.getUTCHours()).padStart(2, '0');
            const mm = String(d.getUTCMinutes()).padStart(2, '0');
            const mon = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'][d.getUTCMonth()];
            const yy = String(d.getUTCFullYear()).slice(-2);
            return `${dd}${hh}${mm}${mon}${yy}`.toUpperCase();
        })(),
        type: 'ICR',
    };

    const baseNumber = data.icrNumber || generateIcrNumber({
        org: (data.owner || data.requestingOrganization || 'ORG').replace(/[^A-Z0-9]/gi, '').slice(0, 8).toUpperCase() || 'ORG',
    });
    const uniqueNumber = await ensureUniqueIcrNumber(baseNumber);
    payload.icrNumber = uniqueNumber;

    if (Array.isArray(payload.eeis) && payload.eeis.length > 0 && !Array.isArray(payload.eeiIds) && !Array.isArray(payload.newEeis)) {
        // Legacy-only create: let the server promote nested eeis[] into first-class rows
        payload.eeis = payload.eeis.map((e) => ({
            text: typeof e === 'string' ? e : (e?.text || ''),
            id: typeof e === 'object' && e?.id ? e.id : undefined,
        })).filter((e) => e.text);
    }
    if (Array.isArray(data.eeiIds)) {
        payload.eeiIds = data.eeiIds;
    }
    if (Array.isArray(data.newEeis)) {
        payload.newEeis = data.newEeis;
    }

    const result = await apiFetch('/api/intelligence-requests', {
        method: 'POST',
        body: JSON.stringify(payload),
    });
    invalidateIcrListCache();
    return result;
}

export async function getIcrByNumber(icrNumber) {
    if (!icrNumber) return null;
    const byNumber = await apiFetch(
        `/api/intelligence-requests/by-icr-number/${encodeURIComponent(icrNumber)}`,
    );
    if (byNumber) return byNumber;
    // Pending proposals have no icrNumber yet — allow opening by document id
    return apiFetch(`/api/intelligence-requests/${encodeURIComponent(icrNumber)}`);
}


/**
 * Propose an ICR from selected FINTEL gap questions (Pending Approval).
 * @param {{reportId: string, gapTexts: string[], title?: string, question?: string, proposedBy?: string, owner?: string}} args
 */
export async function proposeIcrFromFintelGaps({
    reportId,
    gapTexts,
    title,
    question,
    proposedBy,
    owner,
}) {
    const result = await apiFetch('/api/intelligence-requests/proposals/from-fintel-gaps', {
        method: 'POST',
        body: JSON.stringify({reportId, gapTexts, title, question, proposedBy, owner}),
    });
    invalidateIcrListCache();
    return result;
}

/** Approve a pending ICR proposal (sets Active + assigns number). */
export async function approveIcr(id) {
    if (!id) throw new Error('id is required');
    const result = await apiFetch(`/api/intelligence-requests/${encodeURIComponent(id)}/approve`, {
        method: 'POST',
        body: JSON.stringify({}),
    });
    invalidateIcrListCache();
    return result;
}

/** Reject a pending ICR proposal. */
export async function rejectIcr(id, {reason} = {}) {
    if (!id) throw new Error('id is required');
    const result = await apiFetch(`/api/intelligence-requests/${encodeURIComponent(id)}/reject`, {
        method: 'POST',
        body: JSON.stringify({reason: reason || null}),
    });
    invalidateIcrListCache();
    return result;
}

/**
 * Shared, request-coalescing cache for the full ICR ("type=ICR") collection.
 */
const ICR_LIST_LIMIT = 1000;
const ICR_LIST_TTL_MS = 30_000;

let icrListCache = null; // { at: number, data: Array }
let icrListInFlight = null; // Promise<Array>

/**
 * Returns the full ICR collection, using a cached value when fresh and
 * coalescing concurrent loads. Pass `{force: true}` to bypass the cache.
 * @param {{force?: boolean}} [options]
 * @returns {Promise<Array<object>>}
 */
export async function loadIcrList({force = false} = {}) {
    if (!force && icrListCache && (Date.now() - icrListCache.at) < ICR_LIST_TTL_MS) {
        return icrListCache.data;
    }
    if (!force && icrListInFlight) {
        return icrListInFlight;
    }

    icrListInFlight = apiFetch(`/api/intelligence-requests?type=ICR&limit=${ICR_LIST_LIMIT}`)
        .then((data) => {
            const list = Array.isArray(data) ? data : [];
            icrListCache = {at: Date.now(), data: list};
            return list;
        })
        .finally(() => {
            icrListInFlight = null;
        });

    return icrListInFlight;
}

/** Invalidate the shared ICR list cache (call after create/update/delete). */
export function invalidateIcrListCache() {
    icrListCache = null;
}

export async function listIcrs(max = 100) {
    const all = await loadIcrList();
    // Preserve the historical contract of honoring an explicit page size.
    return typeof max === 'number' && max > 0 ? all.slice(0, max) : all;
}

/** Map icrNumber/icrNumber → ICR payload for batch enrichment (e.g. drafts table). */
export async function getIcrMapByNumbers(icrNumbers) {
    const wanted = new Set((icrNumbers || []).filter(Boolean));
    if (!wanted.size) return {};
    const all = await listIcrs(1000);
    const map = {};
    for (const icr of all) {
        const num = resolveIcrNumber(icr);
        if (num && wanted.has(num)) {
            map[num] = icr;
        }
    }
    return map;
}

/**
 * Aggregate ICR coverage snapshot for the detail sidebar (DU1+).
 * GET remains on the dual-read intelligence-requests path.
 */
export async function fetchIcrCoverage(icrId) {
    if (!icrId) {
        return null;
    }
    return apiFetch(`/api/intelligence-requests/${encodeURIComponent(icrId)}/coverage`);
}

/**
 * Enqueue ACC/CCC coverage recompute (async Rqueue). Returns 202 body `{queued:true}`.
 * apiFetch treats 202 as success and parses JSON.
 * @param {string} icrId
 * @param {{scope?: 'collection'|'analytic'|'ars'|'all'}} [options]
 */
export async function recomputeIcrCoverage(icrId, {scope = 'all'} = {}) {
    if (!icrId) {
        return null;
    }
    const qs = scope && scope !== 'all' ? `?scope=${encodeURIComponent(scope)}` : '';
    return apiFetch(`/api/icrs/${encodeURIComponent(icrId)}/coverage/recompute${qs}`, {
        method: 'POST',
    });
}

/**
 * Download standalone HTML coverage report (DU6+).
 */
export async function exportIcrCoverageHtml(icrId) {
    if (!icrId) {
        return;
    }
    const resp = await fetch(
        `${BASE}/api/intelligence-requests/${encodeURIComponent(icrId)}/coverage/export`,
        {headers: authHeaders()},
    );
    if (resp.status === 404) {
        throw new Error('Coverage export is not available yet — wait for scoring to finish.');
    }
    if (!resp.ok) {
        throw new Error(`GET /coverage/export → ${resp.status}`);
    }
    const blob = await resp.blob();
    const disposition = resp.headers.get('Content-Disposition') || '';
    const match = disposition.match(/filename="([^"]+)"/);
    const filename = match ? match[1] : 'coverage_export.html';
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    anchor.click();
    URL.revokeObjectURL(url);
}

/**
 * Batch coverage summary for list cards (DU5+).
 */
export async function fetchCoverageSummary(icrNumbers) {
    const numbers = (icrNumbers || []).filter(Boolean);
    if (numbers.length === 0) {
        return {};
    }
    const query = encodeURIComponent(numbers.join(','));
    return apiFetch(`/api/intelligence-requests/coverage-summary?icrNumbers=${query}`) || {};
}

/**
 * Fetches all approved analytic (FINTEL) reports and returns a map of
 * icrNumber → count, representing how many approved reports cover each ICR.
 */
export async function fetchFintelCoverageMap() {
    let reports;
    try {
        const raw = await apiFetch('/api/reports?productType=analytic&status=approved&limit=1000');
        reports = Array.isArray(raw) ? raw : (Array.isArray(raw?.content) ? raw.content : []);
    } catch (_) {
        return {};
    }
    const map = {};
    for (const r of reports) {
        const icrNum = resolveReportLinkedIcrNumber(r);
        if (!icrNum) continue;
        map[icrNum] = (map[icrNum] || 0) + 1;
    }
    return map;
}

/** Report IDs for analytic/FINTEL products — used to exclude misclassified pitch associations. */
export async function loadAnalyticReportIds() {
    const ids = new Set();
    try {
        const raw = await apiFetch('/api/reports?productType=analytic&limit=2000');
        const reports = Array.isArray(raw) ? raw : (Array.isArray(raw?.content) ? raw.content : []);
        for (const r of reports) {
            if (r?.id) ids.add(r.id);
        }
    } catch (_) { /* non-fatal */ }
    return ids;
}

/**
 * Fetches all pitch reports and returns a map of icrNumber → [{id, title}].
 */
export async function fetchPitchMap() {
    const map = {};
    const fintelIds = await loadAnalyticReportIds();
    try {
        const raw = await apiFetch('/api/reports?productType=pitch&status=approved&limit=500');
        const pitches = Array.isArray(raw) ? raw : (Array.isArray(raw?.content) ? raw.content : []);
        for (const p of pitches) {
            const icrNum = resolveReportLinkedIcrNumber(p);
            if (!icrNum) continue;
            if (!map[icrNum]) map[icrNum] = [];
            const entry = {id: p.id, title: p.title || p.meta?.title || 'Untitled Pitch'};
            if (!map[icrNum].some(e => e.id === entry.id)) map[icrNum].push(entry);
        }
    } catch (_) { /* non-fatal */ }

    try {
        const icrs = await apiFetch('/api/intelligence-requests?type=ICR&limit=500');
        if (Array.isArray(icrs)) {
            for (const icr of icrs) {
                const icrNum = resolveIcrNumber(icr);
                if (!icrNum || !Array.isArray(icr.associations)) continue;
                for (const a of icr.associations) {
                    if (a.itemType !== 'pitch') continue;
                    if (fintelIds.has(a.itemId)) continue;
                    if (!map[icrNum]) map[icrNum] = [];
                    if (!map[icrNum].some(e => e.id === a.itemId)) {
                        map[icrNum].push({id: a.itemId, title: a.itemTitle || 'Untitled Pitch'});
                    }
                }
            }
        }
    } catch (_) { /* non-fatal */ }

    return map;
}

export async function deleteIcrByNumber(icrNumber) {
    if (!icrNumber) return false;
    const doc = await getIcrByNumber(icrNumber);
    if (!doc) return false;
    await apiFetch(`/api/intelligence-requests/${encodeURIComponent(doc.id)}`, {method: 'DELETE'});
    invalidateIcrListCache();
    return true;
}

/**
 * Update an existing ICR by its internal id (UUID).
 * Merges the supplied fields into the existing payload via PUT.
 */
export async function updateIcr(id, data = {}) {
    const result = await apiFetch(`/api/intelligence-requests/${encodeURIComponent(id)}`, {
        method: 'PUT',
        body: JSON.stringify(data),
    });
    invalidateIcrListCache();
    return result;
}

/**
 * Add a pitch / sitrep / whiteboard association to an ICR.
 * @param {string} icrId  - internal UUID of the ICR
 * @param {{itemType: string, itemId: string, itemTitle?: string}} assoc
 */
export async function addIcrAssociation(icrId, {itemType, itemId, itemTitle = ''}) {
    return apiFetch(`/api/intelligence-requests/${encodeURIComponent(icrId)}/associations`, {
        method: 'POST',
        body: JSON.stringify({itemType, itemId, itemTitle}),
    });
}

/**
 * Remove an association by its row id (assocId).
 * @param {string} icrId   - internal UUID of the ICR
 * @param {string} assocId - association row id returned by addIcrAssociation
 */
export async function removeIcrAssociation(icrId, assocId) {
    await apiFetch(
        `/api/intelligence-requests/${encodeURIComponent(icrId)}/associations/${encodeURIComponent(assocId)}`,
        {method: 'DELETE'},
    );
}

/**
 * Search for items that can be linked to an ICR.
 * @param {'pitch'|'sitrep'|'whiteboard'} type
 * @param {string} q - optional search term
 * @returns {Promise<Array<{id: string, title: string, itemType: string}>>}
 */
export async function searchAssociationCandidates(type, q = '') {
    const params = new URLSearchParams({type});
    if (q) params.set('q', q);
    const data = await apiFetch(`/api/intelligence-requests/association-candidates?${params}`);
    return Array.isArray(data) ? data : [];
}

/**
 * Requirement-centric search across ICR questions and EEI texts.
 * @param {{q?: string, priority?: string, org?: string, page?: number, size?: number}} params
 */
export async function searchRequirements(params = {}) {
    const sp = new URLSearchParams();
    if (params.q) sp.set('q', params.q);
    if (params.priority) sp.set('priority', params.priority);
    if (params.org) sp.set('org', params.org);
    if (params.page != null) sp.set('page', String(params.page));
    if (params.size != null) sp.set('size', String(params.size));
    const qs = sp.toString();
    return apiFetch(`/api/intelligence-requests/requirements/search${qs ? `?${qs}` : ''}`);
}

/**
 * ICR | EEI | Gap requirements list with global filters, server-side sort,
 * dual coverage percentages and overlap (FULL/PARTIAL) counts.
 * @param {{entityType?: 'icr'|'eei'|'gap', q?: string, tags?: string[], owners?: string[],
 *          priority?: string|string[], classification?: string|string[],
 *          status?: string|string[],
 *          sortBy?: 'question'|'number'|'author'|'dataCoverage'|'analyticCoverage'|'dueDate'|'overlap',
 *          sortDir?: 'asc'|'desc', page?: number, size?: number}} params
 */
export async function searchRequirementsList(params = {}) {
    const sp = new URLSearchParams();
    if (params.entityType) sp.set('entityType', params.entityType);
    if (params.q) sp.set('q', params.q);
    const asList = (v) => (Array.isArray(v) ? v : (v ? [v] : []));
    for (const tag of asList(params.tags)) sp.append('tags', tag);
    for (const owner of asList(params.owners)) sp.append('owners', owner);
    for (const p of asList(params.priority)) sp.append('priority', p);
    for (const c of asList(params.classification)) sp.append('classification', c);
    // Status is a multiselect any-of (OR) filter, same as tags/owners.
    for (const s of asList(params.status)) sp.append('status', s);
    if (params.sortBy) sp.set('sortBy', params.sortBy);
    if (params.sortDir) sp.set('sortDir', params.sortDir);
    if (params.page != null) sp.set('page', String(params.page));
    if (params.size != null) sp.set('size', String(params.size));
    const qs = sp.toString();
    return apiFetch(`/api/requirements/search${qs ? `?${qs}` : ''}`);
}

/**
 * Load a single Gap by gap number (or id) via the requirements search API.
 * Prefers an exact number/id match among search hits.
 * @param {string} gapNumberOrId
 * @returns {Promise<object|null>}
 */
export async function getGapByNumber(gapNumberOrId) {
    if (!gapNumberOrId) return null;
    const key = String(gapNumberOrId).trim();
    if (!key) return null;
    const data = await searchRequirementsList({
        entityType: 'gap',
        q: key,
        page: 0,
        size: 50,
    });
    const items = Array.isArray(data?.items) ? data.items : [];
    const exact = items.find((item) => (
        String(item.number || '') === key || String(item.id || '') === key
    ));
    return exact || null;
}

/**
 * Set or clear FINTEL gap priority (CM/admin).
 * @param {string} gapId
 * @param {'High'|'Medium'|'Low'|null} priority null clears back to unset ("?")
 */
export async function updateGapPriority(gapId, priority) {
    if (!gapId) throw new Error('gapId is required');
    return apiFetch(`/api/fintel-gaps/${encodeURIComponent(gapId)}/priority`, {
        method: 'PATCH',
        body: JSON.stringify({priority: priority ?? null}),
    });
}

/**
 * Replace Gap Targets (CM/admin). Locks auto-NER for this gap.
 * @param {string} gapId
 * @param {string[]} targets entity refs (QID / D-id)
 * @returns {Promise<{id: string, targets: Array, targetsLocked: boolean}>}
 */
export async function updateGapTargets(gapId, targets) {
    if (!gapId) throw new Error('gapId is required');
    const refs = Array.isArray(targets)
        ? targets.map((t) => (typeof t === 'string' ? t : t?.entityRef)).filter(Boolean)
        : [];
    return apiFetch(`/api/fintel-gaps/${encodeURIComponent(gapId)}/targets`, {
        method: 'PATCH',
        body: JSON.stringify({targets: refs}),
    });
}

/**
 * Related EEIs for a Gap (proposed / promoted). CM/admin.
 * @param {string} gapId
 * @returns {Promise<{gapId: string, eeis: Array<{id: string, eeiNumber?: string, text?: string, status?: string, relationship: 'promoted'|'proposed'}>}>}
 */
export async function getGapRelatedEeis(gapId) {
    if (!gapId) throw new Error('gapId is required');
    return apiFetch(`/api/fintel-gaps/${encodeURIComponent(gapId)}/related-eeis`);
}

/**
 * Create a manual FULL/PARTIAL similarity link (CM/admin).
 * @param {{fromType: string, fromId: string, toType: string, toId: string, relation: 'FULL'|'PARTIAL', comment?: string}} body
 */
export async function createSimilarityEdge(body) {
    return apiFetch('/api/similarity/edges', {
        method: 'POST',
        body: JSON.stringify(body || {}),
    });
}

/**
 * Delete a similarity edge (manual link or machine suggestion; CM/admin).
 * Deleted machine edges reappear as suggestions after the next recompute.
 */
export async function deleteSimilarityEdge(edgeId) {
    return apiFetch(`/api/similarity/edges/${encodeURIComponent(edgeId)}`, {
        method: 'DELETE',
    });
}

/**
 * Edit the comment on a manual similarity link (author only).
 */
export async function updateSimilarityEdgeComment(edgeId, comment) {
    return apiFetch(`/api/similarity/edges/${encodeURIComponent(edgeId)}/comment`, {
        method: 'PATCH',
        body: JSON.stringify({comment}),
    });
}

/**
 * Owner typeahead for the requirements list.
 * Pass entityType `'icr'` / `'eei'` / `'gap'` to only return owners that appear on that tab
 * (avoids ICR Author filter listing EEI-only people with 0 ICRs). Omit for Overview.
 * Returns enriched objects: {owner, userId?, firstName?, lastName?, displayName, hasAvatar, profilePicture?}.
 * Legacy string entries are still accepted by the UI.
 */
export async function suggestRequirementOwners(q = '', limit = 20, entityType) {
    const sp = new URLSearchParams();
    if (q) sp.set('q', q);
    sp.set('limit', String(limit));
    if (entityType) sp.set('entityType', entityType);
    const data = await apiFetch(`/api/requirements/owners?${sp}`);
    return data?.owners || [];
}

/**
 * Tag typeahead for the requirements list: ICR/EEI tags only, ranked by usage count.
 */
export async function suggestRequirementTags(q = '', limit = 20) {
    const sp = new URLSearchParams();
    if (q) sp.set('q', q);
    sp.set('limit', String(limit));
    const data = await apiFetch(`/api/requirements/tags?${sp}`);
    return data?.tags || [];
}

/**
 * Distinct classification values present in ICR payloads (filter value set).
 */
export async function fetchRequirementClassifications() {
    const data = await apiFetch('/api/requirements/classifications');
    return data?.classifications || [];
}

/**
 * Requirements UI feature config (e.g. whether Overview tab is enabled).
 * Returns `{ overviewEnabled }` with default true when the endpoint is missing.
 */
export async function fetchRequirementsConfig() {
    const data = await apiFetch('/api/requirements/config');
    if (data == null) {
        return {overviewEnabled: true};
    }
    return {overviewEnabled: data.overviewEnabled !== false};
}

/**
 * Requirements overview dashboard aggregation (Owner + Tags scoped).
 * Returns null when the endpoint is not yet available (404).
 * @param {{owners?: string[], tags?: string[]}} params
 */
export async function fetchRequirementsOverview(params = {}) {
    const sp = new URLSearchParams();
    const asList = (v) => (Array.isArray(v) ? v : (v ? [v] : []));
    for (const owner of asList(params.owners)) sp.append('owners', owner);
    for (const tag of asList(params.tags)) sp.append('tags', tag);
    const qs = sp.toString();
    return apiFetch(`/api/requirements/overview${qs ? `?${qs}` : ''}`);
}

/**
 * Confirm / reject a similarity edge (CM/admin).
 * @param {{fromType: string, fromId: string, toType: string, toId: string, relation: string, status: 'confirmed'|'rejected'}} body
 */
export async function reviewSimilarityEdge(body) {
    return apiFetch('/api/similarity/edges/review', {
        method: 'POST',
        body: JSON.stringify(body || {}),
    });
}

/**
 * ICR similarity graph + lists (CM/admin). Returns null on 404.
 */
export async function fetchIcrSimilarity(icrId) {
    if (!icrId) return null;
    return apiFetch(`/api/intelligence-requests/${encodeURIComponent(icrId)}/similarity`);
}

/**
 * Enqueue async similarity recompute for an ICR (CM/admin).
 */
export async function recomputeIcrSimilarity(icrId) {
    if (!icrId) return null;
    return apiFetch(`/api/intelligence-requests/${encodeURIComponent(icrId)}/similarity/recompute`, {
        method: 'POST',
        body: '{}',
    });
}

/**
 * Enqueue Rqueue corpus job to recompute similarity for all ICRs, EEIs, and Gaps (CM/admin).
 */
export async function recomputeAllRequirementSimilarity() {
    return apiFetch('/api/similarity/recompute-all', {
        method: 'POST',
        body: '{}',
    });
}

/**
 * Corpus similarity recompute progress (CM/admin).
 */
export async function fetchRequirementSimilarityCorpusStatus() {
    return apiFetch('/api/similarity/recompute-all/status');
}

/**
 * Stop corpus similarity recompute and clear queued similarity tasks (CM/admin).
 */
export async function stopRequirementSimilarityCorpus() {
    return apiFetch('/api/similarity/recompute-all/stop', {
        method: 'POST',
        body: '{}',
    });
}
